import { NumberSeparator } from '@databases/model/custom-number-format';
import { type DatabaseJSON } from '@databases/model/database';
import { type EntryJSON } from '@databases/model/entry';
import {
  BacklinkDisplayType,
  DateFormat,
  DisplayedDetailType,
  type FieldJSON,
  FieldType,
  isAiFieldType,
  NumberFormat,
  ParentPageType
} from '@databases/model/field';
import {
  type AllValueJSON,
  type SingleAttachmentValue,
  type SingleEntryLinkValue,
  type SingleJiraIssueValue,
  type SinglePageLinkValue,
  type SingleUserValue,
  UserType
} from '@databases/model/value';
import {
  CollabCalculationTypes,
  CollabCardWidth,
  type CollabCoverField,
  CollabCoverFieldType,
  CollabLayoutType,
  type DatabaseViewJSON
} from '@databases/model/views/view';
import {
  type CollabFilterJSON,
  CollabFilterType
} from '@databases/model/views/view-filter';
import {
  type CollabGroupingJSON,
  CollabGroupSortType
} from '@databases/model/views/view-grouping';
import { type ObjectMode } from '@databases/types';
import {
  type Database,
  type DatabaseEntry,
  type DatabaseField,
  DatabaseFieldType,
  type DatabaseValue,
  type RawDatabaseValue,
  BacklinkDisplayType as ReduxBacklinkDisplayType,
  DatabaseFieldType as ReduxDatabaseFieldType,
  DisplayedDetailType as ReduxDisplayedDetailType,
  NumberFormats as ReduxNumberFormat,
  NumberSeparators as ReduxNumberSeparator,
  ParentPageType as ReduxParentPageType
} from '@databases/types/database';
import {
  DatabaseCardWidth,
  DatabaseCoverFieldType,
  type DatabaseFilter,
  DatabaseFilterType,
  type DatabaseFilterUsageType,
  DatabaseLayoutType,
  DatabaseGroupSortType,
  type DatabaseGrouping,
  type DatabaseView,
  DatabaseCalculationType
} from '@databases/types/database/view';

import { type DefaultValueConverters } from '../default-value-converters.ts';
import { generateId } from '../helpers/utils.ts';
import { YDatabase } from '../y-database.ts';

export const dbFieldTypeMap = {
  [ReduxDatabaseFieldType.TEXT]: FieldType.Text,
  [ReduxDatabaseFieldType.AI_SUMMARY]: FieldType.AiSummary,
  [ReduxDatabaseFieldType.NUMBER]: FieldType.Number,
  [ReduxDatabaseFieldType.DATE]: FieldType.Date,
  [ReduxDatabaseFieldType.ATTACHMENT]: FieldType.Attachment,
  [ReduxDatabaseFieldType.CHECKBOX]: FieldType.Checkbox,
  [ReduxDatabaseFieldType.BACKLINK]: FieldType.Backlink,
  [ReduxDatabaseFieldType.ENTRY_LINK]: FieldType.EntryLink,
  [ReduxDatabaseFieldType.ENTRY_DETAILS]: FieldType.EntryDetails,
  [ReduxDatabaseFieldType.HYPERLINK]: FieldType.Hyperlink,
  [ReduxDatabaseFieldType.JIRA_ISSUE]: FieldType.JiraIssue,
  [ReduxDatabaseFieldType.JIRA_ISSUE_DETAILS]: FieldType.JiraIssueDetails,
  [ReduxDatabaseFieldType.PAGE_DETAILS]: FieldType.PageDetails,
  [ReduxDatabaseFieldType.PAGE_LABELS]: FieldType.PageLabels,
  [ReduxDatabaseFieldType.PAGE_LINK]: FieldType.PageLink,
  [ReduxDatabaseFieldType.PAGE_STATUS]: FieldType.PageStatus,
  [ReduxDatabaseFieldType.SELECT]: FieldType.Select,
  [ReduxDatabaseFieldType.USER]: FieldType.User
};

export const displayedDetailTypeMap: Record<
  ReduxDisplayedDetailType,
  DisplayedDetailType
> = {
  [ReduxDisplayedDetailType.CREATED_AT]: DisplayedDetailType.CREATED_AT,
  [ReduxDisplayedDetailType.CREATED_BY]: DisplayedDetailType.CREATED_BY,
  [ReduxDisplayedDetailType.LAST_UPDATED_AT]: DisplayedDetailType.LAST_UPDATED_AT,
  [ReduxDisplayedDetailType.LAST_UPDATED_BY]: DisplayedDetailType.LAST_UPDATED_BY,
  [ReduxDisplayedDetailType.EXCERPT]: DisplayedDetailType.EXCERPT_CONTENT,
  [ReduxDisplayedDetailType.CURRENT_VERSION]: DisplayedDetailType.VERSION_NUMBER
};

export const backlinkDisplayTypeMap: Record<
  ReduxBacklinkDisplayType,
  BacklinkDisplayType
> = {
  [ReduxBacklinkDisplayType.LINK]: BacklinkDisplayType.AS_LINK,
  [ReduxBacklinkDisplayType.NUMBER]: BacklinkDisplayType.AS_NUMBER
};

export const numberFormatMap: Record<ReduxNumberFormat, NumberFormat> = {
  [ReduxNumberFormat.NUMBER]: NumberFormat.NUMBER,
  [ReduxNumberFormat.PERCENT]: NumberFormat.PERCENT,
  [ReduxNumberFormat.CUSTOM]: NumberFormat.CUSTOM,
  [ReduxNumberFormat.EURO]: NumberFormat.EURO,
  [ReduxNumberFormat.DOLLAR]: NumberFormat.DOLLAR,
  [ReduxNumberFormat.CURRENCY]: NumberFormat.CURRENCY
};

export const numberSeparatorMap: Record<ReduxNumberSeparator, NumberSeparator> = {
  [ReduxNumberSeparator.APOSTROPHE]: NumberSeparator.APOSTROPHE,
  [ReduxNumberSeparator.COMMA]: NumberSeparator.COMMA,
  [ReduxNumberSeparator.DOT]: NumberSeparator.DOT,
  [ReduxNumberSeparator.WHITESPACE]: NumberSeparator.WHITESPACE
};

export const parentPageTypeMapping: Record<ReduxParentPageType, ParentPageType> = {
  [ReduxParentPageType.PAGE]: ParentPageType.PAGE,
  [ReduxParentPageType.AUTO]: ParentPageType.AUTO,
  [ReduxParentPageType.MACRO]: ParentPageType.MACRO
};

export const CardTypeToCollabMapping = {
  [DatabaseLayoutType.TABLE]: CollabLayoutType.TABLE,
  [DatabaseLayoutType.GALLERY]: CollabLayoutType.GALLERY,
  [DatabaseLayoutType.BOARD]: CollabLayoutType.BOARD,
  [DatabaseLayoutType.CALENDAR]: CollabLayoutType.CALENDAR,
  [DatabaseLayoutType.TIMELINE]: CollabLayoutType.TIMELINE
};

export const CardWidthToCollabMapping = {
  [DatabaseCardWidth.SMALL]: CollabCardWidth.SMALL,
  [DatabaseCardWidth.MEDIUM]: CollabCardWidth.MEDIUM,
  [DatabaseCardWidth.LARGE]: CollabCardWidth.LARGE
};

export const calculationToCollabMapping = {
  [DatabaseCalculationType.SUM]: CollabCalculationTypes.SUM,
  [DatabaseCalculationType.COUNT_TOTAL]: CollabCalculationTypes.COUNT_TOTAL,
  [DatabaseCalculationType.COUNT]: CollabCalculationTypes.COUNT,
  [DatabaseCalculationType.COUNT_EMPTY]: CollabCalculationTypes.COUNT_EMPTY,
  [DatabaseCalculationType.COUNT_UNIQUE]: CollabCalculationTypes.COUNT_UNIQUE,
  [DatabaseCalculationType.PERCENT_EMPTY]: CollabCalculationTypes.PERCENT_EMPTY,
  [DatabaseCalculationType.PERCENT_NOT_EMPTY]: CollabCalculationTypes.PERCENT_NOT_EMPTY,
  [DatabaseCalculationType.AVERAGE]: CollabCalculationTypes.AVERAGE,
  [DatabaseCalculationType.MAX]: CollabCalculationTypes.MAX,
  [DatabaseCalculationType.MIN]: CollabCalculationTypes.MIN
};

export const groupSortTypeToCollabMapping = {
  [DatabaseGroupSortType.ASCENDING]: CollabGroupSortType.ASCENDING,
  [DatabaseGroupSortType.DESCENDING]: CollabGroupSortType.DESCENDING,
  [DatabaseGroupSortType.MANUAL]: CollabGroupSortType.MANUAL
};

const groupingToCollabJSON = (
  grouping: DatabaseGrouping | undefined
): CollabGroupingJSON | undefined => {
  if (!grouping) {
    return undefined;
  }

  const groupBy = grouping.groupBy
    ? {
        ...grouping.groupBy,
        sortType:
          groupSortTypeToCollabMapping[
            grouping.groupBy.sortType as keyof typeof groupSortTypeToCollabMapping
          ]
      }
    : undefined;

  const subgroupBy = grouping.subgroupBy
    ? {
        ...grouping.subgroupBy,
        sortType:
          groupSortTypeToCollabMapping[
            grouping.subgroupBy.sortType as keyof typeof groupSortTypeToCollabMapping
          ]
      }
    : undefined;

  return { ...grouping, groupBy, subgroupBy };
};

export const coverFieldIdToCollabMapping = (coverFieldId: string): CollabCoverField => {
  switch (coverFieldId) {
    case DatabaseCoverFieldType.NONE:
      return { type: CollabCoverFieldType.None };
    case DatabaseCoverFieldType.DEFAULT:
      return { type: CollabCoverFieldType.Default };
    default:
      return {
        type: CollabCoverFieldType.ID,
        id: coverFieldId
      };
  }
};

export const filterUsageTypeToCollabMapping = (type: DatabaseFilterUsageType) => {
  switch (type) {
    case DatabaseFilterType.AND:
      return CollabFilterType.AND;
    case DatabaseFilterType.OR:
      return CollabFilterType.OR;
    default:
      return CollabFilterType.AND;
  }
};

export class CollabConversionError extends Error {
  constructor(message?: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'CollabConversionError';
  }
}

export const covertFieldIdToCollab = (
  coverFieldId: DatabaseCoverFieldType | string
): CollabCoverField => {
  if (coverFieldId === DatabaseCoverFieldType.NONE) {
    return {
      type: CollabCoverFieldType.None
    };
  } else if (
    coverFieldId !== DatabaseCoverFieldType.DEFAULT &&
    typeof coverFieldId === 'string'
  ) {
    return {
      type: CollabCoverFieldType.ID,
      id: coverFieldId
    };
  } else {
    return {
      type: CollabCoverFieldType.Default
    };
  }
};

export const fieldToCollabJSON = (
  dbField: DatabaseField<ReduxDatabaseFieldType>,
  referenceContentIdMap: Record<string, string>,
  throwOnMissingMapEntry = true
): FieldJSON => {
  const destType = dbFieldTypeMap[dbField.type];
  if (!destType) {
    throw new CollabConversionError('unsupported field type ' + dbField.type);
  }

  let parentPage: string | undefined;
  if (dbField.settings.parentPage) {
    parentPage = dbField.settings.parentPage.contentId;
  }

  let targetDbContentId: string | undefined;
  if (dbField.settings.databaseId) {
    targetDbContentId = referenceContentIdMap[`database:${dbField.settings.databaseId}`];

    if (
      !targetDbContentId &&
      throwOnMissingMapEntry &&
      [DatabaseFieldType.ENTRY_LINK, DatabaseFieldType.BACKLINK].includes(dbField.type)
    ) {
      throw new CollabConversionError(
        'missing databaseId ' + dbField.settings.databaseId + ' in referenceContentIdMap'
      );
    }
  } else if (
    dbField.settings.contentId &&
    referenceContentIdMap[`database:${dbField.settings.contentId}`]
  ) {
    targetDbContentId = referenceContentIdMap[`database:${dbField.settings.contentId}`];

    if (
      !targetDbContentId &&
      throwOnMissingMapEntry &&
      [DatabaseFieldType.ENTRY_LINK, DatabaseFieldType.BACKLINK].includes(dbField.type)
    ) {
      throw new CollabConversionError(
        'missing contentId ' + dbField.settings.contentId + ' in referenceContentIdMap'
      );
    }
  } else if (dbField.settings.contentId) {
    targetDbContentId = dbField.settings.contentId;
  }

  return {
    type: destType,
    name: dbField.name,
    description: dbField.description || '',
    showDescription: dbField.showDescription || false,
    width: dbField.width,
    aiFieldConfig:
      isAiFieldType(destType) && dbField.settings.aiFieldConfig
        ? dbField.settings.aiFieldConfig
        : undefined,
    aiRunMetadata: isAiFieldType(destType) ? dbField.aiRunMetadata : undefined,
    multi: dbField.settings.multi,
    resolve: dbField.settings.resolve,
    allowEdit: dbField.settings.allowEdit,
    databaseId: targetDbContentId,
    displayedDetail: displayedDetailTypeMap[dbField.settings.displayedDetail],
    contentField: dbField.settings.contentField,
    backlinkDisplayType: backlinkDisplayTypeMap[dbField.settings.backlinkDisplayType],
    fieldId: dbField.settings.fieldId,
    includeVersionComment: dbField.settings.includeVersionComment,
    creatable: dbField.settings.creatable,
    entryFieldField: dbField.settings.lookupFieldId,
    pageTemplateId: dbField.settings.pageTemplate.templateId,
    pageSubType: dbField.settings.pageSubType,
    parentPage,
    parentPageOption: dbField.settings.parentPageOption,
    isParentPageSourceDatabase: dbField.settings.isParentPageSourceDatabase,
    displayEntryOnPage: dbField.settings.displayEntryOnPage,
    parentPageType: parentPageTypeMapping[dbField.settings.parentPageType],
    jiraField: dbField.settings.jiraField,
    jiraServerId: dbField.settings.jiraServerId,
    mediaFormat: dbField.settings.mediaFormat,
    cloudId: dbField.settings.cloudId,
    numberFormat: numberFormatMap[dbField.settings.numberFormat.format],
    customNumberFormat: {
      minFractionDigits: dbField.settings.numberFormat.minimumFractionDigits,
      maxFractionDigits: dbField.settings.numberFormat.maximumFractionDigits,
      decimalPlaces: dbField.settings.numberFormat.decimalPlaces,
      decimalSeparator:
        numberSeparatorMap[dbField.settings.numberFormat.decimalSeparator],
      thousandsSeparator: dbField.settings.numberFormat.thousandsSeparator
        ? numberSeparatorMap[dbField.settings.numberFormat.thousandsSeparator]
        : undefined,
      prefix: dbField.settings.numberFormat.prefix,
      suffix: dbField.settings.numberFormat.suffix
    },
    selectOptions: dbField.settings.selectOptions.map((option) => ({
      id: option.id ?? option.label,
      color: option.color,
      label: option.label
    })),
    defaultEnabled: dbField.settings.defaultEnabled,
    mentionNotificationEnabled: dbField.settings.mentionNotificationEnabled,
    dateFormat:
      destType === FieldType.Date
        ? (dbField.settings.dateFormat ?? DateFormat.DEFAULT)
        : undefined,
    timeFormat: dbField.settings.timeFormat,
    enableDateRange: dbField.settings.enableDateRange
  };
};

export const fieldsByIdToCollabJSON = (
  fieldsById: Database['fieldsById'],
  referenceContentIdMap: Record<string, string>,
  throwOnMissingMapEntry = true
): DatabaseJSON['fieldsById'] =>
  Object.keys(fieldsById).reduce(
    (acc, fieldId) => {
      acc[fieldId] = fieldToCollabJSON(
        fieldsById[fieldId],
        referenceContentIdMap,
        throwOnMissingMapEntry
      );
      return acc;
    },
    {} as DatabaseJSON['fieldsById']
  );

export const valueToCollabJSON = (
  dbValue: DatabaseValue<ReduxDatabaseFieldType>,
  referenceContentIdMap: Record<string, string>
): AllValueJSON | undefined => {
  switch (dbValue.type) {
    case ReduxDatabaseFieldType.TEXT:
      return {
        type: FieldType.Text,
        value: dbValue.value
      };
    case ReduxDatabaseFieldType.AI_SUMMARY:
      return {
        type: FieldType.AiSummary,
        value: dbValue.value
      };
    case ReduxDatabaseFieldType.NUMBER:
      return {
        type: FieldType.Number,
        value: dbValue.value
      };
    case ReduxDatabaseFieldType.DATE:
      return {
        type: FieldType.Date,
        value: dbValue.value || ''
      };
    case ReduxDatabaseFieldType.SELECT:
      return {
        type: FieldType.Select,
        value: dbValue.value
      };
    case ReduxDatabaseFieldType.USER:
      return {
        type: FieldType.User,
        value: convertUserToCollab(dbValue.value)
      };
    case ReduxDatabaseFieldType.CHECKBOX:
      return {
        type: FieldType.Checkbox,
        value: dbValue.value
      };
    case ReduxDatabaseFieldType.ATTACHMENT:
      return {
        type: FieldType.Attachment,
        value: convertAttachmentToCollab(dbValue.value, referenceContentIdMap)
      };
    case ReduxDatabaseFieldType.PAGE_LINK:
      return {
        type: FieldType.PageLink,
        value: convertPageLinkToCollab(dbValue.value, referenceContentIdMap)
      };
    case ReduxDatabaseFieldType.HYPERLINK:
      return {
        type: FieldType.Hyperlink,
        value: dbValue.value.map((val) => ({
          id: val._id || generateId(),
          url: val.url
        }))
      };
    case ReduxDatabaseFieldType.ENTRY_LINK:
      return {
        type: FieldType.EntryLink,
        value: convertEntryLinkToCollab(dbValue.value)
      };
    case ReduxDatabaseFieldType.JIRA_ISSUE:
      return {
        type: FieldType.JiraIssue,
        value: convertJiraIssueToCollab(dbValue.value)
      };
    case ReduxDatabaseFieldType.PAGE_LABELS:
    case ReduxDatabaseFieldType.PAGE_STATUS:
    case ReduxDatabaseFieldType.PAGE_DETAILS:
    case ReduxDatabaseFieldType.ENTRY_DETAILS:
    case ReduxDatabaseFieldType.BACKLINK:
    case ReduxDatabaseFieldType.JIRA_ISSUE_DETAILS:
      return undefined;
  }
};

const convertUserToCollab = (
  value: RawDatabaseValue<ReduxDatabaseFieldType.USER>
): SingleUserValue[] => {
  if (typeof value === 'string') {
    return [{ type: UserType.Current }];
  }

  const res: SingleUserValue[] = [];
  value.forEach((user) => {
    res.push({
      id: user._id,
      ...('accountId' in user && { accountId: user.accountId }),
      type: UserType.Referenced
    });
  });
  return res;
};

const convertEntryLinkToCollab = (
  values: RawDatabaseValue<ReduxDatabaseFieldType.ENTRY_LINK>
): SingleEntryLinkValue[] => {
  const res: SingleEntryLinkValue[] = [];
  values.forEach((val) => {
    if ('entryId' in val) {
      res.push({ entryId: val.entryId, id: val._id });
    }
  });

  return res;
};

const convertPageLinkToCollab = (
  values: RawDatabaseValue<ReduxDatabaseFieldType.PAGE_LINK>,
  referenceContentIdMap: Record<string, string>
): SinglePageLinkValue[] => {
  const res: SinglePageLinkValue[] = [];
  values.forEach((val) => {
    let contentId = 'contentId' in val ? val.contentId : undefined;
    const ari = 'ari' in val ? val.ari : undefined;

    if (!contentId && 'type' in val) {
      contentId =
        referenceContentIdMap[`page:${val.spaceKey}:${val.contentTitle}`] ||
        referenceContentIdMap[`page:${val.contentTitle}`];
    }

    if (contentId) {
      res.push({ id: val._id, contentId, ari });
    }
  });

  return res;
};

const convertAttachmentToCollab = (
  values: RawDatabaseValue<ReduxDatabaseFieldType.ATTACHMENT>,
  referenceContentIdMap: Record<string, string>
): SingleAttachmentValue[] => {
  const res: SingleAttachmentValue[] = [];
  values.forEach((val) => {
    let fileId;
    if (values && 'fileId' in val) {
      fileId = val.fileId;
    } else if (values && 'filename' in values) {
      fileId = referenceContentIdMap[`attachment:${values.filename}`];
    }
    if (fileId) {
      res.push({ id: val._id, fileId });
    }
  });
  return res;
};

const convertJiraIssueToCollab = (
  values: RawDatabaseValue<ReduxDatabaseFieldType.JIRA_ISSUE>
): SingleJiraIssueValue[] => {
  const res: SingleJiraIssueValue[] = [];
  values.forEach((val) => {
    if ('key' in val) {
      res.push({ key: val.key, id: val._id });
    }
  });

  return res;
};

export const cardWidthToCollab = (cardWidth: DatabaseCardWidth): CollabCardWidth => {
  switch (cardWidth) {
    case DatabaseCardWidth.SMALL:
      return CollabCardWidth.SMALL;
    case DatabaseCardWidth.MEDIUM:
      return CollabCardWidth.MEDIUM;
    case DatabaseCardWidth.LARGE:
      return CollabCardWidth.LARGE;
  }
};

export const filterToCollabJSON = (
  filter: DatabaseFilter,
  referenceContentIdMap: Record<string, string>
): CollabFilterJSON | undefined => {
  switch (filter.type) {
    case DatabaseFilterType.AND:
      return {
        id: filter.id,
        type: CollabFilterType.AND,
        value: filter.value.flatMap((filter) => {
          const collabFilter = filterToCollabJSON(filter, referenceContentIdMap);
          return collabFilter ? [collabFilter] : [];
        })
      };
    case DatabaseFilterType.OR:
      return {
        id: filter.id,
        type: CollabFilterType.OR,
        value: filter.value.flatMap((filter) => {
          const collabFilter = filterToCollabJSON(filter, referenceContentIdMap);
          return collabFilter ? [collabFilter] : [];
        })
      };
    case DatabaseFilterType.EQUALS: {
      const collabValue = valueToCollabJSON(filter.value, referenceContentIdMap);
      if (collabValue) {
        return {
          id: filter.id,
          type: CollabFilterType.EQUALS,
          value: collabValue,
          not: filter.not,
          fieldId: filter.fieldId
        };
      } else {
        return undefined;
      }
    }
    case DatabaseFilterType.CONTAINS:
      return {
        id: filter.id,
        type: CollabFilterType.CONTAINS,
        fieldId: filter.fieldId,
        value: filter.value,
        not: filter.not
      };
    case DatabaseFilterType.EMPTY:
      return {
        id: filter.id,
        type: CollabFilterType.EMPTY,
        fieldId: filter.fieldId,
        not: filter.not
      };
    case DatabaseFilterType.GREATER: {
      const collabValue = valueToCollabJSON(filter.value, referenceContentIdMap);
      if (collabValue) {
        return {
          id: filter.id,
          type: CollabFilterType.GREATER,
          fieldId: filter.fieldId,
          orEqual: filter.orEqual,
          value: collabValue
        };
      } else {
        return undefined;
      }
    }
    case DatabaseFilterType.LESS: {
      const collabValue = valueToCollabJSON(filter.value, referenceContentIdMap);
      if (collabValue) {
        return {
          id: filter.id,
          type: CollabFilterType.LESS,
          fieldId: filter.fieldId,
          orEqual: filter.orEqual,
          value: collabValue
        };
      } else {
        return undefined;
      }
    }
  }
};

export const calculationsToCollabJSON = (
  databaseCalculations: Record<string, DatabaseCalculationType> | undefined
): Record<string, CollabCalculationTypes> => {
  if (!databaseCalculations) {
    return {};
  }
  return Object.keys(databaseCalculations).reduce(
    (acc, key) => {
      acc[key] = calculationToCollabMapping[databaseCalculations[key]];
      return acc;
    },
    {} as Record<string, CollabCalculationTypes>
  );
};

export const viewToCollabJSON = (
  view: DatabaseView,
  referenceContentIdMap: Record<string, string>
): DatabaseViewJSON => {
  return {
    name: view.name,
    layoutType: CardTypeToCollabMapping[view.settings.layout.type],
    cardWidth: cardWidthToCollab(view.settings.layout.cardWidth),
    coverField: covertFieldIdToCollab(view.settings.layout.coverFieldId),
    showFieldNames: view.settings.layout.showFieldNames,
    groupFieldId: view.settings.layout.groupFieldId,
    dateFieldId: view.settings.layout.dateFieldId,
    showNumberedEntries: view.settings.layout.showNumberedEntries,
    ...(view.settings.properties && {
      properties: view.settings.properties
    }),
    sort: view.settings.sort,
    fields: view.settings.fields,
    filter: view.settings.filter
      ? filterToCollabJSON(view.settings.filter, referenceContentIdMap)
      : undefined,
    calculations: calculationsToCollabJSON(view.settings.calculations),
    sorts: view.settings.sorts,
    grouping: groupingToCollabJSON(view.settings.grouping)
  };
};

export const viewsToViewsByIdCollabJSON = (
  views: DatabaseView[],
  referenceContentIdMap: Record<string, string>
): DatabaseJSON['viewsById'] => {
  return views.reduce(
    (obj, view) => {
      obj[view.id] = viewToCollabJSON(view, referenceContentIdMap);
      return obj;
    },
    {} as DatabaseJSON['viewsById']
  );
};

export function entryToCollabJSON(
  dbEntry: DatabaseEntry,
  referenceContentIdMap: Record<string, string>
): EntryJSON {
  return {
    values: Object.keys(dbEntry.values).reduce(
      (acc, fieldId) => {
        const collabValue = valueToCollabJSON(
          dbEntry.values[fieldId],
          referenceContentIdMap
        );
        if (collabValue) {
          acc[fieldId] = collabValue;
        }
        return acc;
      },
      {} as Record<string, AllValueJSON>
    )
  };
}

export function entriesByIdToCollabJSON(
  entriesById: Database<ObjectMode.CREATE | ObjectMode.EXISTING>['entriesById'],
  referenceContentIdMap: Record<string, string>
): DatabaseJSON['entriesById'] {
  return Object.keys(entriesById).reduce(
    (acc, entryId) => {
      acc[entryId] = entryToCollabJSON(entriesById[entryId], referenceContentIdMap);
      return acc;
    },
    {} as DatabaseJSON['entriesById']
  );
}

export function convertToYjs(
  db: Database<ObjectMode.CREATE | ObjectMode.EXISTING>,
  defaultValueConverters: DefaultValueConverters,
  referenceContentIdMap: Record<string, string>,
  throwOnMissingMapEntry = true
): YDatabase {
  return YDatabase.fromJSON(
    {
      fields: db.fields,
      fieldsById: fieldsByIdToCollabJSON(
        db.fieldsById,
        referenceContentIdMap,
        throwOnMissingMapEntry
      ),
      views: db.views.map((view) => view.id),
      viewsById: viewsToViewsByIdCollabJSON(db.views, referenceContentIdMap),
      entries: db.entries,
      entriesById: entriesByIdToCollabJSON(db.entriesById, referenceContentIdMap),
      defaultViewId: db.defaultViewId,
      defaultEntry: entryToCollabJSON(db.defaultEntry, referenceContentIdMap),
      isLocked: db.meta.isLocked,
      commentThreads: db.commentThreads
    },
    defaultValueConverters
  );
}
